/**
 * @file index.tsx
 *
 * @fileoverview Displays the form that handles searching for fulltext and region autocomplete.
 */
import React, { useEffect, useRef, useState, useTransition } from 'react';
import FulltextSearch from '~/jobiqo/components/Search/FulltextSearch';
import { GeoLocationAutocomplete } from '~/components/Form/LocationAutocomplete';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearch } from '~/jobiqo/components/Search/hooks/useSearch';
import { getGeoInputFromUrl } from '~/jobiqo/components/Search/utils/searchHelpers';
import styles from './searchInput.module.scss';
import { FormItemsInline } from '~/vcl/molecules/01-form/03-form-items-inline';
import { Select } from '~/vcl/atoms/01-forms/05-select';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface SearchInputFormProps {
  /**
   * The current querystring object.
   */
  querystring?: any;
  /**
   * The endpoint for the fulltext search.
   */
  endpoint: string;
  /**
   * Search URL page as per config.
   */
  searchUrl: string;
  /**
   * Geo information based on config.
   */
  geoInfo: any;
  /**
   * If the geo search should be hidden.
   *
   * @default false
   */
  hideGeoSearch?: boolean;
  /**
   * If the keyword search should be hidden.
   *
   * @default false
   */
  hideKeywordSearch?: boolean;
  /**
   * The radius search flag.
   *
   * @default true
   */
  radiusSearch?: boolean;
}

export const SearchInputForm = ({
  querystring,
  endpoint,
  searchUrl,
  geoInfo,
  hideGeoSearch = false,
  hideKeywordSearch = false,
  radiusSearch = true,
}: SearchInputFormProps) => {
  const [_, startTransition] = useTransition();
  const intl = useIntl();
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const {
    redirectWithoutFulltextAndLocation,
    redirectWithFulltextAndOrLocation,
  } = useSearch({
    searchUrl: searchUrl,
  });

  let location: any = '';
  if (querystring?.geo_location) {
    location = getGeoInputFromUrl(querystring);
  }

  const [keyword, setKeyword] = useState(
    querystring?.search ? querystring.search : '',
  );
  const [geoLocation, setGeolocation] = useState(location ? location : null);

  const [radius, setRadius] = useState(
    !geoLocation
      ? geoInfo.radius.defaultValue
      : parseInt(geoLocation.radius, 10),
  );

  // As default state for geoLocation is set only once at the beginning and is not updated after user
  // perform search or click to the search page link again, we need to set geoLocation to null when there
  // is no geo_location parameter in the querystring. UseEffect is used because we need to change it only
  // when "location" is updated, as geoLocation is updated also during user input.
  // It also sets geoLocation when a location is added to the URL (e.g. clicking a job's location link).
  useEffect(() => {
    setGeolocation(location ? location : null);
    if (location?.radius) {
      setRadius(parseInt(location.radius, 10));
    }
  }, [querystring?.geo_location, querystring?.radius]);

  const [submit, setSubmit] = useState(false);

  // Track when a suggestion was just selected to prevent onChange from
  // overwriting the coordinates in the same React batch (race condition fix).
  const justSelectedSuggestion = useRef(false);

  const radiusOptions = geoInfo.radius.options.map((option) => {
    return {
      code: `${option * geoInfo.radius.unitMultiplier}`,
      name: `${option} ${geoInfo.radius.unit}`,
    };
  });

  // The default value for the radius depends on if there is a location in the url (e.g. /california)
  // and if that location has a radius.
  const [locationHasRadius, setLocationHasRadius] = useState(true);

  const handleSubmit = (event) => {
    startTransition(() => {
      event.preventDefault();
      setSubmit(true);
    });
  };

  useEffect(() => {
    if (submit) {
      if (!geoLocation && !keyword) {
        redirectWithoutFulltextAndLocation();
        return;
      } else {
        redirectWithFulltextAndOrLocation(keyword, geoLocation);
      }
      setSubmit(false);
    }
  }, [submit, keyword, geoLocation, radius]);

  useEffect(() => {
    // Show or hide the radius select depending on whether the search result
    // was provided by a radius search or not.
    setLocationHasRadius(radiusSearch);
  }, [radiusSearch]);

  return (
    <form
      className={styles.wrapper}
      data-test-id="search-form"
      onSubmit={handleSubmit}
    >
      <FormItemsInline
        inputs={[
          !hideKeywordSearch && (
            <FulltextSearch
              key="search_keywords"
              fulltext={querystring?.search}
              onChange={setKeyword}
              onSubmit={(keywordValue) => {
                setKeyword(keywordValue);
                setSubmit(true);
              }}
              endpoint={endpoint}
            />
          ),
          !hideGeoSearch && (
            <GeoLocationAutocomplete
              name="search_geolocation"
              key="search_geolocation"
              label={intl.formatMessage({
                id: 'location_autocomplete_label',
                defaultMessage: `Enter location`,
              })}
              placeholder={intl.formatMessage({
                id: 'location_autocomplete_placeholder',
                defaultMessage: `Enter location`,
              })}
              {...(geoLocation
                ? { locationStr: geoLocation.label, autoLoadGoogleMaps: true }
                : null)}
              onChange={(label) => {
                // After onSuggestSelect fires, the geosuggest library updates the
                // input value, triggering onChange in the same React batch. Skip
                // this onChange to avoid overwriting the coordinates just set by
                // onSuggestSelect with empty lat/lon.
                if (justSelectedSuggestion.current) {
                  justSelectedSuggestion.current = false;
                  return;
                }
                // To allow resolving a location when someone just types
                // something in the URL. The backend can try to resolve this location.
                const emptyItem = {
                  label,
                  radius: geoInfo.radius.defaultValue,
                  lat: '',
                  lon: '',
                  country: '',
                  administrativeArea: '',
                  locationType: '',
                };
                setGeolocation(emptyItem);
              }}
              onSuggestSelect={(item) => {
                // Check if item exists to prevent redirecting to job search
                // in case user delete all characters from input field.
                if (item) {
                  // Set flag before setGeolocation so that the onChange triggered
                  // by the geosuggest library updating the input value (which fires
                  // in the same synchronous batch) does not overwrite coordinates.
                  justSelectedSuggestion.current = true;
                  setGeolocation(item);
                  if (item?.radius) {
                    setRadius(item.radius);
                    if (
                      item.locationType &&
                      geoInfo?.radius?.hideForLocationType
                    ) {
                      setLocationHasRadius(
                        !geoInfo?.radius?.hideForLocationType?.includes(
                          item.locationType,
                        ),
                      );
                    }
                  }

                  setSubmit(true);
                }
              }}
              fixtures={geoInfo.fixtures}
              isSearchForm
              regionModal={false}
              radiusParams={geoInfo.radius}
              types={geoInfo.types}
            />
          ),
          !hideGeoSearch && location && locationHasRadius && (
            <Select
              id="radius"
              items={radiusOptions}
              selectedValue={radius.toString()}
              onChange={(value) => {
                if (value) {
                  setRadius(value.code);

                  if (geoLocation) {
                    const locationWithRadius = {
                      ...geoLocation,
                      radius: value.code,
                    };

                    // Update location with the newly selected radius.
                    setGeolocation(locationWithRadius);
                  }
                } else {
                  setRadius(
                    geoInfo.radius.defaultValue * geoInfo.radius.unitMultiplier,
                  );
                }
                setSubmit(true);
              }}
            />
          ),
        ].filter(Boolean)}
        button={
          <Button
            type="submit"
            buttonStyle={frontendTheme.SEARCH_FORM.BUTTON_STYLE}
          >
            <span>
              <FormattedMessage
                id="search_inputs_submit"
                defaultMessage={`Search`}
              />
            </span>
          </Button>
        }
      />
    </form>
  );
};
export default SearchInputForm;
